import type { CertInfo } from './sign-types';
import { showToast } from '../../utils/show-toast';

declare global {
  interface Window {
    bootstrap?: BootstrapNamespace;
  }
}

type BootstrapModalInstance = {
  show(): void;
  hide(): void;
};

type BootstrapNamespace = {
  Modal: new (element: HTMLElement) => BootstrapModalInstance;
};

type ReportFormSignViewElements = {
  modal: HTMLElement;
  docName: HTMLElement;
  docPeriod: HTMLElement;
  docGeneratedAt: HTMLElement;
  certSelect: HTMLSelectElement;
  certDetails: HTMLElement;
  certListBlock: HTMLElement;
  noCertsAlert: HTMLElement;
  helpLink: HTMLAnchorElement;
  spinner: HTMLElement;
  confirmButton: HTMLButtonElement;
  successModal: HTMLElement;
  successSignedAt: HTMLElement;
  successSerialNumber: HTMLElement;
  successSigner: HTMLElement;
  successDownloadButton: HTMLButtonElement;
};

export class ReportFormSignView {
  private readonly elements: ReportFormSignViewElements;
  private selectedThumbprint: string | null = null;
  private certCache: CertInfo[] = [];
  private signBootstrapModal: BootstrapModalInstance | null = null;
  private successBootstrapModal: BootstrapModalInstance | null = null;
  private static readonly PLUGIN_UNAVAILABLE_MESSAGE =
    'Сертификаты электронной подписи не найдены. Убедитесь, что КриптоПро CSP и браузерный плагин установлены, а носитель ключа (токен/смарт-карта) подключён к компьютеру.';

  constructor(root: HTMLElement) {
    this.elements = {
      modal: this.getElement<HTMLElement>(root, '[data-sign-cert-modal]'),
      docName: this.getElement<HTMLElement>(root, '[data-sign-doc-name]'),
      docPeriod: this.getElement<HTMLElement>(root, '[data-sign-doc-period]'),
      docGeneratedAt: this.getElement<HTMLElement>(root, '[data-sign-doc-generated-at]'),
      certSelect: this.getElement<HTMLSelectElement>(root, '[data-sign-cert-select]'),
      certDetails: this.getElement<HTMLElement>(root, '[data-sign-cert-details]'),
      certListBlock: this.getElement<HTMLElement>(root, '[data-sign-cert-list-block]'),
      noCertsAlert: this.getElement<HTMLElement>(root, '[data-sign-no-certs-alert]'),
      helpLink: this.getElement<HTMLAnchorElement>(root, '[data-sign-help-link]'),
      spinner: this.getElement<HTMLElement>(root, '[data-sign-spinner]'),
      confirmButton: this.getElement<HTMLButtonElement>(root, '[data-sign-cert-confirm]'),
      successModal: this.getElement<HTMLElement>(root, '[data-sign-success-modal]'),
      successSignedAt: this.getElement<HTMLElement>(root, '[data-sign-success-signed-at]'),
      successSerialNumber: this.getElement<HTMLElement>(root, '[data-sign-success-serial-number]'),
      successSigner: this.getElement<HTMLElement>(root, '[data-sign-success-signer]'),
      successDownloadButton: this.getElement<HTMLButtonElement>(root, '[data-sign-success-download]'),
    };

    this.signBootstrapModal = this.resolveBootstrapModal(this.elements.modal);
    this.successBootstrapModal = this.resolveBootstrapModal(this.elements.successModal);
    this.bindDismiss();
    this.bindSuccessDismiss();
    this.bindCertSelectChange();
  }

  bindConfirm(handler: (thumbprint: string | null) => void): void {
    this.elements.confirmButton.addEventListener('click', () => {
      handler(this.selectedThumbprint);
    });
  }

  setHelpUrl(url: string): void {
    this.elements.helpLink.href = url;
  }

  setDocumentInfo(
    documentName: string,
    reportPeriod: string,
    generatedAt: string,
    pdfUrl?: string | null,
  ): void {
    const name = documentName || 'Отчёт.pdf';
    const el = this.elements.docName;
    el.textContent = name;
    if (el instanceof HTMLAnchorElement) {
      if (pdfUrl) {
        try {
          el.href = new URL(pdfUrl, window.location.href).href;
        } catch {
          el.href = pdfUrl;
        }
        el.target = '_blank';
        el.rel = 'noopener noreferrer';
        el.classList.add('link-primary');
      } else {
        el.removeAttribute('href');
        el.removeAttribute('target');
        el.removeAttribute('rel');
        el.classList.remove('link-primary');
      }
    }
    this.elements.docPeriod.textContent = reportPeriod || '-';
    const generatedDate = new Date(generatedAt);
    this.elements.docGeneratedAt.textContent = Number.isNaN(generatedDate.getTime())
      ? generatedAt || '-'
      : generatedDate.toLocaleString();
  }

  renderCerts(certs: CertInfo[]): void {
    this.certCache = certs;
    this.selectedThumbprint = null;
    this.elements.certSelect.innerHTML = '';
    this.elements.certDetails.textContent = '';
    this.elements.noCertsAlert.classList.add('d-none');
    this.elements.certListBlock.classList.remove('d-none');

    if (certs.length === 0) {
      this.elements.certListBlock.classList.add('d-none');
      this.elements.noCertsAlert.classList.remove('d-none');
      return;
    }

    certs.forEach((cert, index) => {
      const option = document.createElement('option');
      option.value = cert.thumbprint;
      option.text = `${parseSubjectName(cert.subjectName)} | ${cert.serialNumber}`;
      if (index === 0) {
        option.selected = true;
        this.selectedThumbprint = cert.thumbprint;
      }
      this.elements.certSelect.append(option);
    });

    this.renderCertDetails();
  }

  findCertByThumbprint(thumbprint: string): CertInfo | undefined {
    return this.certCache.find((cert) => cert.thumbprint === thumbprint);
  }

  setModalProcessing(isProcessing: boolean): void {
    this.elements.spinner.classList.toggle('d-none', !isProcessing);
    this.elements.spinner.classList.toggle('d-flex', isProcessing);
    this.elements.confirmButton.disabled = isProcessing;
  }

  showSuccess(
    _message: string,
    downloadUrl: string,
    signedAt: string,
    serialNumber: string,
    signerName: string,
  ): void {
    this.elements.successSignedAt.textContent = formatSignedAt(signedAt);
    this.elements.successSerialNumber.textContent = serialNumber || '-';
    this.elements.successSigner.textContent = signerName || '-';
    this.elements.successDownloadButton.onclick = () => {
      window.location.href = downloadUrl;
    };
    this.hideModal();
    this.openSuccessModal();
  }

  resetSuccessState(): void {
    this.elements.confirmButton.classList.remove('d-none');
  }

  showError(message: string): void {
    if (this.isPluginUnavailableMessage(message)) {
      this.elements.noCertsAlert.classList.remove('d-none');
      return;
    }

    showToast(message, 'failed');
  }

  clearError(): void {
    this.elements.noCertsAlert.classList.add('d-none');
  }

  openModal(): void {
    this.clearError();
    this.resetSuccessState();
    this.hideSuccessModal();
    if (this.signBootstrapModal) {
      this.signBootstrapModal.show();
      return;
    }
    this.elements.modal.classList.add('show');
    this.elements.modal.style.display = 'block';
  }

  hideModal(): void {
    if (this.signBootstrapModal) {
      this.signBootstrapModal.hide();
      return;
    }
    this.elements.modal.classList.remove('show');
    this.elements.modal.style.display = 'none';
  }

  openSuccessModal(): void {
    if (this.successBootstrapModal) {
      this.successBootstrapModal.show();
      return;
    }
    this.elements.successModal.classList.add('show');
    this.elements.successModal.style.display = 'block';
  }

  hideSuccessModal(): void {
    if (this.successBootstrapModal) {
      this.successBootstrapModal.hide();
      return;
    }
    this.elements.successModal.classList.remove('show');
    this.elements.successModal.style.display = 'none';
  }

  private bindDismiss(): void {
    const dismissButtons = this.elements.modal.querySelectorAll<HTMLElement>(
      '[data-bs-dismiss="modal"], [data-dismiss="modal"], .btn-close'
    );
    dismissButtons.forEach((button) => {
      button.addEventListener('click', (event) => {
        event.preventDefault();
        this.hideModal();
      });
    });
  }

  private bindSuccessDismiss(): void {
    const dismissButtons = this.elements.successModal.querySelectorAll<HTMLElement>(
      '[data-bs-dismiss="modal"], [data-dismiss="modal"], .btn-close'
    );
    dismissButtons.forEach((button) => {
      button.addEventListener('click', (event) => {
        event.preventDefault();
        this.hideSuccessModal();
      });
    });
  }

  private bindCertSelectChange(): void {
    this.elements.certSelect.addEventListener('change', () => {
      this.selectedThumbprint = this.elements.certSelect.value || null;
      this.renderCertDetails();
    });
  }

  private renderCertDetails(): void {
    if (!this.selectedThumbprint) {
      this.elements.certDetails.textContent = '';
      return;
    }
    const cert = this.certCache.find((item) => item.thumbprint === this.selectedThumbprint);
    if (!cert) {
      this.elements.certDetails.textContent = '';
      return;
    }
    const validToDate = new Date(cert.validTo);
    const isValid = Number.isFinite(validToDate.getTime()) && validToDate.getTime() > Date.now();
    const marker = isValid ? 'действителен' : 'истёк/недействителен';
    this.elements.certDetails.textContent = [
      `Владелец: ${parseSubjectName(cert.subjectName)}`,
      `Организация: ${parseOrgName(cert.subjectName) || '-'}`,
      `Серийный номер: ${cert.serialNumber}`,
      `Срок действия до: ${new Date(cert.validTo).toLocaleString()} (${marker})`,
    ].join(' | ');
  }

  private getElement<T extends HTMLElement>(root: HTMLElement, selector: string): T {
    const element = root.querySelector<T>(selector) ?? document.querySelector<T>(selector);
    if (!element) {
      throw new Error(`Не найден обязательный элемент: ${selector}`);
    }
    return element;
  }

  private resolveBootstrapModal(modalElement: HTMLElement): BootstrapModalInstance | null {
    const bootstrapNamespace = window.bootstrap;
    if (!bootstrapNamespace?.Modal) {
      return null;
    }
    return new bootstrapNamespace.Modal(modalElement);
  }

  private isPluginUnavailableMessage(message: string): boolean {
    const normalized = message.toLowerCase();
    return (
      normalized.includes(
        ReportFormSignView.PLUGIN_UNAVAILABLE_MESSAGE.toLowerCase(),
      ) || normalized.includes('сертификаты электронной подписи не найдены')
    );
  }
}

function parseSubjectName(subjectName: string): string {
  const cn = extractDnValue(subjectName, 'CN');
  return cn || subjectName;
}

function parseOrgName(subjectName: string): string {
  return extractDnValue(subjectName, 'O');
}

function extractDnValue(subjectName: string, key: string): string {
  const parts = subjectName.split(',');
  const found = parts.find((part) => part.trim().startsWith(`${key}=`));
  return found ? found.split('=').slice(1).join('=').trim() : '';
}

function formatSignedAt(signedAt: string): string {
  const signedDate = new Date(signedAt);
  if (Number.isNaN(signedDate.getTime())) {
    return signedAt || '-';
  }
  return signedDate.toLocaleString();
}
